import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { IBindingCallback1, IBindingAction } from '../../common/models/callback';
import { IAppState } from '../../common/models/store/IAppState';
import {
  fetchTeamsRoutine,
  createTeamRoutine,
  updateTeamRoutine,
  deleteTeamRoutine,
  getTeamUsersCountRoutine
} from './routines';
import SearchInput from '../../components/SearchInput';
import RegularButton from '../../components/RegularButton';
import CheckboxList, { ICheckboxListHandler } from '../../components/CheckboxList';
import { ITeam } from '../../common/models/team/ITeam';
import { map, filter, pipe, find, any, append, all } from 'ramda';
import LoaderWrapper from '../../components/LoaderWrapper';
import { Node } from '../../common/models/common/Node';
import Icon from '../../components/Icon';
import { IUser } from '../../common/models/user/IUser';
import { RoleType } from '../../common/enums/RoleType';
import styles from './styles.module.scss';
import Input from '../../components/Input';
import IconButton from '../../components/IconButton';
import { usePrevious } from '../../common/helpers/customHooks';

interface IProps {
  loading: boolean;
  teams: ITeam[] | null;
  selectedIds?: string[];
  multipleChoice?: boolean;
  advanced?: boolean;
  isAdmin: boolean;
  saving: boolean;
  deletable: boolean;
  onSelect: IBindingCallback1<ITeam[]>;
  fetchTeams: IBindingAction;
  createTeam: IBindingCallback1<string>;
  updateTeam: IBindingCallback1<ITeam>;
  deleteTeam: IBindingCallback1<string>;
  getUsersCount: IBindingCallback1<string>;
}

const TeamList: FunctionComponent<IProps> = ({
  advanced = false,
  isAdmin,
  teams,
  loading,
  selectedIds = [],
  multipleChoice = false,
  saving,
  onSelect,
  fetchTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  getUsersCount,
  deletable
}) => {
  const NEW_TEAM_ID = 'new';
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>(selectedIds);
  const [updatedTeam, setUpdatedTeam] = useState<ITeam | null>(null);
  const listRef = useRef<ICheckboxListHandler>(null);

  useEffect(() => {
    if (!teams) {
      fetchTeams();
    }
  }, []);

  useEffect(() => {
    if (updatedTeam) {
      setUpdatedTeam(null);
    }
  }, [selected]);

  const onCheck = (options: string[]) => {
    setSelected(options);
    const selectedTeams = filter(({ id }) => any(val => val === id, options), teams as ITeam[]);
    onSelect(selectedTeams);
  };

  const prevSaving = usePrevious(saving);
  useEffect(() => {
    if (prevSaving && !saving) {
      setUpdatedTeam(null);
      const updatedSelectedIds = filter(
        selectedId => Boolean(find(({ id }) => selectedId === id, teams as ITeam[])),
        selected
      );
      listRef.current?.changeSelected(updatedSelectedIds);
    }
  }, [saving]);

  const parsedSearch = search.trim().toLowerCase();

  const getTeamLabel = ({ id, name }: ITeam) => (
    id === updatedTeam?.id
      ? (
        <Input
          className={styles.updateTeamInput}
          value={updatedTeam.name}
          onChange={val => setUpdatedTeam(state => ({ ...(state as ITeam), name: val }))}
          onClick={event => event.stopPropagation()}
          autoFocus
          variant="underlined"
        />
      )
      : (
        <div className={styles.teamOption}>
          <span className="overflow-ellipsis">{name}</span>
          {advanced && isAdmin && (
            <IconButton
              icon={<Icon icon="pencil" />}
              variant="blue"
              onClick={event => {
                event.stopPropagation();
                setUpdatedTeam({ id, name });
              }}
            />
          )}
        </div>
      )
  );

  const allTeams = updatedTeam?.id === NEW_TEAM_ID ? append(updatedTeam, teams || []) : teams;

  const nodes = allTeams ? pipe<ITeam[], ITeam[], Node[]>(
    filter(({ name, id }: ITeam) => id === NEW_TEAM_ID || name.toLowerCase().includes(parsedSearch)),
    map(team => ({
      label: getTeamLabel(team),
      value: team.id
    }))
  )(allTeams) : [];

  const defaultChecked = pipe(
    map(id => {
      const node = find(({ value }) => value === id, nodes);
      return node?.value as string;
    }),
    filter(val => Boolean(val))
  )(selectedIds);

  const onShowAll = () => {
    listRef.current?.changeSelected([]);
    onCheck([]);
  };

  const isSavedTeamValid = () => {
    const teamName = updatedTeam?.name;
    return (
      Boolean(teamName?.trim())
      && all(({ name }) => name.toLowerCase() !== teamName?.trim().toLowerCase(), teams as ITeam[])
    );
  };

  const onSaveTeam = () => {
    const { id, name } = updatedTeam as ITeam;
    const trimmedName = name.trim();
    if (id === NEW_TEAM_ID) {
      createTeam(trimmedName);
    } else {
      updateTeam({ id, name: trimmedName });
    }
  };

  const onAddTeam = () => {
    setUpdatedTeam({ id: NEW_TEAM_ID, name: '' });
  };

  useEffect(() => {
    if (updatedTeam) {
      if (updatedTeam.id !== NEW_TEAM_ID) {
        getUsersCount(updatedTeam.id);
      } else {
        listRef.current?.scrollToItem(nodes.length - 1, 'smart');
      }
    }
  }, [updatedTeam]);

  return (
    <div className={`${styles.teamList} team-list`}>
      <div className={styles.body}>
        <SearchInput
          intlId="teamList.search"
          defaultPlaceholder="Search teams"
          autoFocus
          value={search}
          onChange={setSearch}
        />
        <div>
          {advanced && multipleChoice && (
            <div
              role="presentation"
              onClick={onShowAll}
              className={`${styles.showAllItem} ${styles.item} rct-text`}
            >
              <Icon className={styles.icon} icon={selected.length ? 'radioUnchecked' : 'radioChecked'} />
              <div className={styles.label}>
                <FormattedMessage id="teamList.showAll" defaultMessage="Show all teams" />
              </div>
            </div>
          )}
          <div className={styles.treeWrapper}>
            <LoaderWrapper loading={loading || !teams}>
              <CheckboxList
                ref={listRef}
                loading={loading}
                total={nodes.length}
                items={nodes}
                onSelect={onCheck}
                defaultChecked={defaultChecked}
                multipleChoice={multipleChoice}
              />
            </LoaderWrapper>
          </div>
        </div>
      </div>
      {advanced && isAdmin && !updatedTeam && (
        <footer className={styles.footer}>
          <RegularButton
            textIntlId="teamList.addTeam"
            defaultMessage="Add team"
            size="sm"
            variant="secondary"
            onClick={onAddTeam}
          />
        </footer>
      )}
      {updatedTeam && (
        <footer className={styles.footer}>
          {updatedTeam.id !== NEW_TEAM_ID && (
            <RegularButton
              textIntlId="delete"
              defaultMessage="Delete"
              size="sm"
              variant="tertiary"
              onClick={() => deleteTeam(updatedTeam.id)}
              disabled={!deletable}
              tooltip={deletable ? undefined : {
                id: 'teamList.deleteDisabled',
                defaultMessage: 'Must move users to different team before deleting'
              }}
            />
          )}
          <RegularButton
            textIntlId="cancel"
            defaultMessage="Cancel"
            size="sm"
            variant="tertiary"
            onClick={() => setUpdatedTeam(null)}
          />
          <RegularButton
            textIntlId="save"
            defaultMessage="Save"
            size="sm"
            onClick={onSaveTeam}
            disabled={!isSavedTeamValid()}
          />
        </footer>
      )}
    </div>
  );
};

const mapStateToProps = ({
  teamList: { teams, loading, saving, deletable },
  user: { user }
}: IAppState) => ({ teams, loading, saving, isAdmin: (user as IUser).role !== RoleType.User, deletable });

const mapDispachToProps = {
  fetchTeams: fetchTeamsRoutine,
  createTeam: createTeamRoutine,
  updateTeam: updateTeamRoutine,
  deleteTeam: deleteTeamRoutine,
  getUsersCount: getTeamUsersCountRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(TeamList);
